// ** React Imports
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// ** MUI Imports
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { toast, ToastContainer } from "react-toastify";

// ** Custom Component Imports
import CustomTextField from "components/mui/text-field";
import CustomAutocomplete from "components/mui/autocomplete";

// ** API Imports
import { SaveRole } from "../logic/SaveRole";

const AddRole = ({ open, handleClose, RoleProps }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    existingRole,
    fetchRoles,
    roleContent,
    onRoleCreated, 
  } = RoleProps;

  const [role, setRole] = useState({
    roleID: 0,
    roleName: "",
    roleType: "select",
    roleDescription: "",
    isActive: true,
  });

  const [errors, setErrors] = useState({
    roleName: false,
    roleNameMsg: '',
    roleType: false,
  });

  useEffect(() => {
    if(roleContent.entity === 'create'){
      resetRole()
    }

    if(roleContent.entity === 'edit'){
      setRole(existingRole)
    }
  }, [open])
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setRole((prevRole) => ({
      ...prevRole,
      [name] : value
    }))
  }

  const validateRoleName = (roleName) => {
    if (!roleName?.trim()) {
      return { error: true, message: 'Role Name is required' };
    }
  
    const normalized = roleName.toLowerCase()
      .replace(/[^a-z]/g, '')
      .replace(/([a-z])\1+/g, '$1');
  
    if (/superadmin/.test(normalized)) {
      return { error: true, message: 'Role Name not allowed' };
    }
  
    return { error: false, message: '' };
  };

  const handleSave = async () => {
    const roleNameError = validateRoleName(role.roleName);

    const newErrors = {
      ...errors,
      roleName: roleNameError.error,
      roleNameMsg: roleNameError.message,
      roleType: !role.roleType || role.roleType?.trim() === 'select'
    };
  
    setErrors(newErrors);
  
    if (Object.values(newErrors).some(error => error !== false && error !== '')) {
      toast.error("Please fill out all required fields", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
      return;
    }

    try {
      const result = await SaveRole(role, navigate);
      if (result && !result.isError) {
        toast.success("Role created successfully", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
         if (fetchRoles) {
        await fetchRoles(0);
      }
      
      if (onRoleCreated) {
        onRoleCreated();
      }
      
      resetRole();
      handleClose();
      } else {
        toast.error(result?.message || "Failed to save role", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
      }
    } catch (error) {
      console.error("Error saving role:", error);
      toast.error("An error occurred while saving the role", {
        style: {
          fontFamily: '"DM Sans", sans-serif',
          fontSize: '15px',
        },
      });
    }
  };

  const resetRole = () => {
    setRole({
      roleID: 0,
      roleName: "",
      roleType: "select",
      roleDescription: "",
      isActive: true,
    })
  }

  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth="xs"
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid #D9D9D9",
            paddingBottom: "20px !important",
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {roleContent.title}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={5}>
            <Grid item xs={12} md={8}>
              <CustomTextField
                fullWidth
                required
                label="Role Name"
                name='roleName'
                value={role.roleName}
                placeholder="Enter your desired role name"
                onChange={handleChange}
                error={errors.roleName}
                helperText={errors.roleName ? errors.roleNameMsg : ''}
              />
            </Grid>
           <Grid item xs={12} md={8}>
            <CustomAutocomplete
              fullWidth
              required
              options={[
                { value: 'Admin', label: 'Admin' },
                { value: 'Manager', label: 'Manager' },
                { value: 'TeamLead', label: 'Crew Leader' },
                { value: 'TeamMember', label: 'Crew' }
              ].sort((a, b) => a.label.localeCompare(b.label))}
              value={role.roleType && role.roleType !== 'select' ? { value: role.roleType, label: role.roleType === 'TeamLead' ? 'Crew Leader' : role.roleType === 'TeamMember' ? 'Crew' : role.roleType } : null}
              onChange={(event, newValue) => {
                if (newValue?.value !== 'select') {
                  handleChange({
                    target: {
                      name: 'roleType',
                      value: newValue?.value || ''
                    }
                  });
                }
              }}
              getOptionLabel={(option) => option.label}
              renderInput={(params) => (
                <CustomTextField
                  {...params}
                  label="Role Type"
                  placeholder="Select Role Type"
                  required
                  name="roleType"
                  error={Boolean(errors.roleType)}
                  helperText={errors.roleType ? 'Role Type is required' : ''}
                />
              )}
              PaperProps={{
                sx: {
                  maxHeight: 200
                }
              }}
            />
          </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            padding: "20px 24px !important",
            boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
            zIndex: 1,
          }}
          className="dialog-actions-dense"
        >
          <Box>
            <Button variant="contained" color="info" onClick={handleClose}>
              Cancel
            </Button>
          </Box>
          <Box>
        <Button variant="contained" color="main" onClick={handleSave}>
          {roleContent.entity === 'edit' ? 'Save Changes' : 'Save Role'}
        </Button>


          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddRole;
